import { BottomSheet, DialogFooter, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Link } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { StickyFooter } from "@src/appV2/redesign/components/StickyFooter";
import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

import { type PlacementDetail } from "../../api/useGetPlacement";
import { PayDetailsStepContainer } from "./PayDetailsStepContainer";
import { type ApplicationPreferencesFormData } from "./WorkerPreferencesStep";
import { WorkerPreferencesStepContainer } from "./WorkerPreferencesStepContainer";

interface PlacementApplicationPreferenceFormDialogUIProps {
  modalState: UseModalState;
  placementCandidateId: string;
  placementId: string;
  workerId: string;
  workplaceId: string;
  applicationQualification: string | undefined;
  placementApplicationMethod: PlacementDetail["applicationMethod"];
  onSavePreferences: (data: ApplicationPreferencesFormData) => void;
  steps: Array<"workerPreferences" | "payDetails">;
  step: "workerPreferences" | "payDetails";
  setStep: (step: "workerPreferences" | "payDetails") => void;
}

export function PlacementApplicationPreferenceFormDialogUI(
  props: PlacementApplicationPreferenceFormDialogUIProps
) {
  const {
    modalState,
    placementCandidateId,
    applicationQualification,
    onSavePreferences,
    placementId,
    placementApplicationMethod,
    workerId,
    workplaceId,
    steps,
    step,
    setStep,
  } = props;

  const completedRef = useRef<string[]>([]);

  const addCompletedStep = (stepName: "workerPreferences" | "payDetails") => {
    completedRef.current = [...new Set([...completedRef.current, stepName])];
  };

  const {
    trigger,
    getValues,
    formState: { isSubmitting },
  } = useFormContext<ApplicationPreferencesFormData>();

  useEffect(() => {
    return () => {
      const formValuesToLog = getValues();

      logEvent(APP_V2_APP_EVENTS.PLACEMENT_APPLICATION_PREFERENCE_FORM_DETAILS, {
        placementCandidateId,
        applicationQualification,
        steps,
        values: formValuesToLog,
        completedSteps: completedRef.current,
        incompleteSteps: steps.filter((step) => !completedRef.current.includes(step)),
        isCompleted: completedRef.current.length === steps.length,
        placementId,
        workerId,
        workplaceId,
      });
    };
  }, [
    applicationQualification,
    getValues,
    placementCandidateId,
    placementId,
    steps,
    workerId,
    workplaceId,
  ]);

  const handleApplicationPreferenceSubmit = () => {
    onSavePreferences(getValues());
    modalState.closeModal();
  };

  const handleWorkerPreferencesNext = async () => {
    const valid = await trigger();
    if (!valid) {
      return;
    }

    addCompletedStep(step);

    const nextStep = steps[steps.indexOf(step) + 1];
    if (isDefined(nextStep)) {
      setStep(nextStep);
    } else {
      handleApplicationPreferenceSubmit();
    }
  };

  const handlePayDetailsNext = () => {
    addCompletedStep(step);
    handleApplicationPreferenceSubmit();
  };

  const handlePayDetailsBack = () => {
    const previousStep = steps[steps.indexOf(step) - 1];
    setStep(previousStep);
  };

  const handleClose = () => {
    modalState.closeModal();
  };

  return (
    <BottomSheet modalState={modalState} header={<BottomSheetHeader onClose={handleClose} />}>
      {step === "workerPreferences" && (
        <WorkerPreferencesStepContainer
          applicationQualification={applicationQualification}
          placementCandidateId={placementCandidateId}
        />
      )}

      {step === "payDetails" && (
        <PayDetailsStepContainer applicationQualification={applicationQualification} />
      )}

      {/* Sticky Footer CTA actions */}
      {step === "workerPreferences" && (
        <StickyFooter>
          <DialogFooter orientation="vertical" LinkComponent={Link}>
            <LoadingButton
              fullWidth
              variant="contained"
              size="large"
              isLoading={isSubmitting}
              onClick={handleWorkerPreferencesNext}
            >
              Confirm and continue
            </LoadingButton>
          </DialogFooter>
        </StickyFooter>
      )}

      {step === "payDetails" && (
        <StickyFooter>
          <DialogFooter orientation="vertical" LinkComponent={Link}>
            <LoadingButton
              fullWidth
              variant="contained"
              size="large"
              isLoading={false}
              onClick={handlePayDetailsNext}
            >
              {placementApplicationMethod === "apply_in_app" ? "Apply to job" : "Next"}
            </LoadingButton>

            <LoadingButton
              fullWidth
              variant="outlined"
              size="large"
              isLoading={false}
              onClick={handlePayDetailsBack}
            >
              Back
            </LoadingButton>
          </DialogFooter>
        </StickyFooter>
      )}
    </BottomSheet>
  );
}
